import { useState, useMemo, useEffect } from 'react';
import FunnelLayout from '~pages/FunnelV2/components/shared/FunnelLayout';
import Select from '~components/Form/components/Select';
import { CleanFiltersIcon } from '~misc/icons';

import ActiveWorkTable from '../components/ActiveWork/ActiveWorkTable';
import ActiveWorkChart from '../components/ActiveWork/ActiveWorkChart';
import { addVolumesFaturados } from '../helpers/volumesFaturados';
import { db } from '~db/db';
import useFunilObraUnidadeMedida from '~fetchs/Funnel/useFunilObraUnidadeMedida';
import useFunilObraGrupoProduto from '~fetchs/useFunilObraGrupoProduto';
import useFunilStatus from '~fetchs/useFunilStatus';
import { formatDateByPattern, getMeasure } from '../helpers/funnelHelpers';
import { formatMonth } from '../helpers/utils';
import { useSearchParams } from 'react-router-dom';
import { chartPeriodOptions, getChartMonthRange, isInChartMonthRange } from '../helpers/chart/ChartPeriod';

const FunnelActiveWorks = () => {
  const [productGroup, setProductGroup] = useState("todos")
  const [period, setPeriod] = useState('default');
  const monthRange = useMemo(() => getChartMonthRange(period), [period]);
  const [clients, setClients] = useState({});
  const [funnel, setFunnel] = useState([]);
  const [accordionData, setAccordionData] = useState([])
  const [revenues, setRevenues] = useState({});

  const { funilObraUnidadeMedidaObjeto } = useFunilObraUnidadeMedida({});
  const { optionFunilObraGruposProduto, funilObraGrupoProdutoObjeto } = useFunilObraGrupoProduto({});
  const { funilStatusObjBySlug, funilStatusObj } = useFunilStatus({});
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';

  const handleClear = () => {
    setPeriod('default');
    setProductGroup('')
    setSearchParams((currentParams) => {
      const params = new URLSearchParams(currentParams);
      params.delete('search');
      return params;
    });
  }

  const getClients = async () => {
    const clienteIds = [...new Set(await db.funilObra
      .orderBy('clienteId')
      .keys())];

    const clientes = {};

    await db.cliente.where('id').anyOf(clienteIds).each(e => {
      clientes[`id-${e.id}`] = {
        codigo: e.codigo,
        nome: e.razaoSocial,
        grupoCentralizadorSearch: String(e.grupoCentralizador ?? '').toLowerCase(),
        isProspect: e.clienteProspect == 1,
      }
    });

    setClients(clientes);
  }

  const setFunnelData = async () => {
    const items = await getFunnel();
    setFunnel(items);
  }

  const getFunnel = async () => {
    let data = [];
    await db.funilObra.filter(e => !e.deletedAt).each(e => data.push(e));

    return data;
  }

  const productGroupOptions = useMemo(() => ([
    { value: 'todos', label: 'Todos' },
    ...optionFunilObraGruposProduto,
  ]), [optionFunilObraGruposProduto]);

  const filteredTable = useMemo(() => {
    const status = funilStatusObjBySlug['ganha']?.id ?? 0;
    const searchValue = search.toLowerCase().trim();


    return (status ? accordionData : [])
      .map((client) => {
        const filteredProdutos = client.produtos.filter((p) => {

          const matchGroup =
            (!productGroup || [null, "", "todos"].includes(productGroup)
              ? true
              : p.funilObraGrupoProdutoId == productGroup) &&
              isInChartMonthRange(p.dataNegociacaoOriginal, monthRange) &&
            (status === p.funilObraStatusId)
          return matchGroup;
        })
        return { ...client, produtos: filteredProdutos }
      })
      .filter((client) => {
        if (!client.produtos.length) return false;
        if (!searchValue) return true;

        return (
          client.nome?.toLowerCase().includes(searchValue) ||
          client.codigo?.toString().includes(searchValue) ||
          client.grupoCentralizadorSearch?.includes(searchValue)
        );
      });
  }, [productGroup, accordionData, funilStatusObjBySlug, search, revenues, monthRange ]);

  const graphData = useMemo(() => {
    const temp = {};

    filteredTable.forEach(a => {
      (a.produtos || []).forEach(cur => {
        const date = new Date(cur.dataNegociacaoOriginal);
        const month = date.getMonth() + 1;
        const formattedDate = formatDateByPattern(date, "mm-yyyy");

        const groupId = cur.funilObraGrupoProdutoId ?? 0;
        const group = funilObraGrupoProdutoObjeto[`id-${groupId}`];
        const measure = funilObraUnidadeMedidaObjeto[`id-${cur.funilObraUnidadeMedidaId}`];

        if (group && measure) {
          const formattedMeasure = getMeasure(measure.nome ?? "") ?? "";
          temp[formattedDate] ??= { 
            formattedDate,
            monthNumber: month, 
            label: `${formatMonth(month).substring(0, 3)}/${formatDateByPattern(date, "yyyy")}`, 
            groups: {},
            tempPercent: [],
            media: 0
          };

          temp[formattedDate].tempPercent.push(parseFloat(cur.porcentagem ?? 0));

          temp[formattedDate].groups[`id-${groupId}`] ??= { 
            volume: 0, 
            slug: group.nome.slugify(), 
            label: `${group.nome} (${formattedMeasure})`
          };

          temp[formattedDate].groups[`id-${groupId}`].volume += cur.volumeFaturado;
          const total = temp[formattedDate].tempPercent.reduce((acc, cur) => {
            acc += cur;
            return acc;
          },0);
          
          temp[formattedDate].media = total / temp[formattedDate].tempPercent.length;
        }
      });
    });

    const sortedKeys = Object.keys(temp).sort((a, b) => 
    {
      const [monthA, yearA] = temp[a].formattedDate.split("-").map(Number);
      const [monthB, yearB] = temp[b].formattedDate.split("-").map(Number);

      const valueA = yearA * 100 + monthA;
      const valueB = yearB * 100 + monthB;

      return valueA - valueB;
    });
    const graphDataResult = sortedKeys.map(k => temp[k]);

    return graphDataResult;
  }, [filteredTable, funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funilStatusObj]);

  useEffect(() => {
    if(
      Object.entries(funilStatusObjBySlug ?? {})?.length &&
      Object.entries(clients ?? {})?.length &&
      Object.entries(funilObraGrupoProdutoObjeto ?? {})?.length &&
      Object.entries(funilObraUnidadeMedidaObjeto ?? {})?.length &&
      funnel?.length
    ) {
        const formattedFunnel = funnel.reduce((acc, cur) => {
          const key = cur.funilObraGrupoProdutoId ?? 0;
          const client = clients[`id-${cur.clienteId}`] ?? null;

          const group = funilObraGrupoProdutoObjeto[`id-${key}`];

          const measure = funilObraUnidadeMedidaObjeto[`id-${cur.funilObraUnidadeMedidaId}`];

          const date = new Date(cur.dataNegociacao);

          if (group && measure) {
            acc[cur.clienteId] ??= {
              clienteId: cur.clienteId,
              codigo: client?.codigo,
              nome: client?.nome,
              grupoCentralizadorSearch: client?.grupoCentralizadorSearch,
              isProspect: client?.isProspect ?? false,
              produtos: [],
            }

            const billedVolume = revenues[cur.clienteId]?.[`id-${key}`] ?? 0;
            const percentage = Number.isFinite(cur.volumeNegociado / billedVolume) ? Math.round((cur.volumeNegociado / billedVolume) * 100) : 0;

            acc[cur.clienteId].produtos.push({
              id: cur.id,
              funilObraGrupoProdutoId: cur.funilObraGrupoProdutoId,
              funilObraStatusId: cur.funilStatusId,
              funilObraUnidadeMedidaId: cur.funilObraUnidadeMedidaId,
              grupoProduto: group.nome,
              volumeFaturado: billedVolume,
              volumeNegociado: cur.volumeNegociado,
              dataNegociacao: formatDateByPattern(date, "dd/mm/YYYY"),
              dataNegociacaoOriginal: cur.dataNegociacao,
              status: funilStatusObj[`id-${cur.funilStatusId}`]?.nome ?? 0,
              porcentagem: percentage,
              hasWarning: false
            })
          }

          return acc;
        }, {});

        setAccordionData(Object.values(formattedFunnel));
      }
  }, [funilObraGrupoProdutoObjeto, funilObraUnidadeMedidaObjeto, funilStatusObj, funnel, clients, revenues])

  useEffect(() => {
    (async () => {
      const status = funilStatusObjBySlug['ganha']?.id ?? 0;

      if (!status || !funnel.length || !Object.keys(funilObraGrupoProdutoObjeto).length) return;
      const clientIds = [...new Set(funnel.filter(e => e.funilStatusId == status).map(e => e.clienteId))];

      const funnelGroupsByCodigo = new Map(Object.values(funilObraGrupoProdutoObjeto).map(group => [group.codigo, group]));

      const groups = {};

      await db.grupoProdutos.each(group => {
        const funnelGroup = funnelGroupsByCodigo.get(group.codigo);

        if (funnelGroup) {
          groups[group.id] = {
            funnelGroupId: funnelGroup.id
          };
        }
      });

      const data = {};

      await db.faturamentos.where('clienteId').anyOf(clientIds)
      .each(faturamento => {
          const group = groups[faturamento.grupoMaterialId];

          addVolumesFaturados(data, faturamento, group, funilObraGrupoProdutoObjeto);
        });

      setRevenues(data);
    })();
  }, [funnel, funilObraGrupoProdutoObjeto, funilStatusObjBySlug]);

  useEffect(() => {
    (async() => {        
        await setFunnelData();
        await getClients();
    })()
  }, []);

  return (
    <FunnelLayout>
      <div className="container mt-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="w-52">
            <Select
              options={productGroupOptions}
              label="Grupo de Produto:"
              name="productGroup"
              value={productGroupOptions.find((o) => o.value === productGroup) ?? null}
              onChange={(opt) => setProductGroup(opt?.value ?? '')}
              placeholder="Selecione"
              spacingControl={true}
            />
          </div>
          <div className="w-52">
            <Select
              label="Período:"
              name="chartPeriod"
              options={chartPeriodOptions}
              value={chartPeriodOptions.find((option) => option.value === period)}
              onChange={(option) => setPeriod(option?.value ?? "default")}
              spacingControl
            />
          </div>
          <div className="mb-3 ml-auto">
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-2 text-sm font-semibold text-primaria"
            >
              <span>Limpar Filtros</span>
              <CleanFiltersIcon />
            </button>
          </div>
        </div>
      </div>

      <ActiveWorkChart data={graphData} productGroup={productGroup} />

      <ActiveWorkTable data={filteredTable} />
    </FunnelLayout>
  );
};

export default FunnelActiveWorks;
